import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, FileSignature, UserCheck } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { PageHero } from "../components/site/Blocks";
import { Reveal, SectionHeading } from "../components/site/Primitives";
import { BusinessEnquiryForm } from "../components/forms/BusinessEnquiryForm";
import { SERVICES } from "../data/services";

const AUDIENCE = ["Chief Financial Officers", "Founders and CEOs", "Chief Information Officers", "General Counsel", "Internal Audit Leaders", "Compliance Heads", "Procurement Teams", "Transformation Directors"];
const ENGAGEMENTS = [
  { t: "Focused projects", d: "Defined deliverables over a set period, such as a review, assessment or analysis." },
  { t: "Interim expertise", d: "Experienced professionals filling a temporary capability gap or leadership role." },
  { t: "Co-sourced support", d: "Specialists working alongside your in-house team on specific workstreams." },
  { t: "Expert review", d: "An independent second opinion on a plan, document, model or approach." },
];
const MATCHING = ["Submit your request", "We clarify your requirements", "Suitability and conflicts assessment", "Shortlist for your review", "Introductions you choose", "Engagement terms agreed"];
const TRUST = [
  { icon: Lock, t: "Confidentiality", d: "Information you share is used only to assess your request. Details are shared with potential professionals on a need-to-know basis, and confidentiality arrangements can be agreed before sensitive information is exchanged." },
  { icon: UserCheck, t: "Conflicts considerations", d: "Professionals are asked to disclose known conflicts before introduction. Regulated professionals also apply their own conflict procedures. Please let us know of any parties that should be excluded." },
  { icon: FileSignature, t: "How engagement terms are established", d: "Scope, deliverables, fees and responsibilities are agreed in writing under the applicable engagement model before work begins. Professionals remain responsible for their own qualifications and obligations." },
];

export default function ForBusinesses() {
  return (
    <>
      <Seo title="For Businesses" description="Access vetted independent auditors, consultants, advisers, tax professionals and legal practitioners without maintaining every capability in-house. Submit a business request to Trusted Leap." path="/for-businesses" />
      <PageHero eyebrow="For businesses" title="Specialised expertise, without carrying every capability in-house." intro="Trusted Leap is a professional resource for organisations that need focused, senior expertise for specific challenges — introduced through a curated, confidential process." image="/images/collaboration.jpg" imageAlt="Executives collaborating in a meeting room">
        <a href="#request" className="btn-primary" data-testid="biz-hero-submit-link">Submit a Business Request</a>
      </PageHero>

      <section className="bg-white py-24 lg:py-28">
        <div className="container-x grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5"><SectionHeading eyebrow="Who we serve" title="Built for decision-makers responsible for outcomes." /></div>
          <ul className="grid grid-cols-2 gap-px self-start overflow-hidden rounded-lg border border-slate-200 bg-slate-200 lg:col-span-7">
            {AUDIENCE.map((a) => <li key={a} className="bg-white px-6 py-6 font-display text-[15px] font-semibold text-[#0B1220]">{a}</li>)}
          </ul>
        </div>
      </section>

      <section className="bg-[#F4F6F8] py-24 lg:py-28">
        <div className="container-x">
          <SectionHeading eyebrow="Engagements supported" title="Types of engagement." />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {ENGAGEMENTS.map((e, i) => (
              <Reveal key={e.t} delay={i * 0.06} className="card-line p-8 hover:-translate-y-1">
                <h3 className="font-display text-lg font-semibold text-[#0B1220]">{e.t}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{e.d}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-16 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group flex items-center justify-between rounded-md border border-slate-300 bg-white px-5 py-4 text-sm font-semibold text-[#0B1220] transition-colors hover:border-[#2563EB]" data-testid={`biz-discipline-${s.slug}`}>
                <span className="flex items-center gap-3"><s.icon className="h-4 w-4 text-[#2563EB]" strokeWidth={1.6} /> {s.name}</span>
                <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-[#2563EB]" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-28">
        <div className="container-x">
          <SectionHeading eyebrow="The matching process" title="How clients are matched." />
          <ol className="mt-14 grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-6">
            {MATCHING.map((m, i) => (
              <li key={m} className="bg-white p-6"><span className="text-xs font-semibold text-[#2563EB]">0{i + 1}</span><p className="mt-6 font-display text-[15px] font-semibold leading-snug text-[#0B1220]">{m}</p></li>
            ))}
          </ol>
          <div className="mt-16 grid gap-10 lg:grid-cols-3">
            {TRUST.map((t) => (
              <div key={t.t}>
                <t.icon className="h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                <h3 className="mt-5 font-display text-xl font-semibold text-[#0B1220]">{t.t}</h3>
                <p className="mt-3 leading-relaxed text-slate-600">{t.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="request" className="scroll-mt-24 border-t border-slate-200 bg-[#F4F6F8] py-24 lg:py-28" data-testid="biz-request-section">
        <div className="container-x grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="Business request" title="Submit a Business Request" intro="Share as much relevant context as you can. A member of the team will review your request and contact you about next steps." />
            <p className="mt-8 text-sm leading-relaxed text-slate-500">Submitting a request does not create an engagement or obligation. Professional services are provided only under agreed engagement terms.</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-6 sm:p-10 lg:col-span-8">
            <BusinessEnquiryForm idPrefix="biz" />
          </div>
        </div>
      </section>
    </>
  );
}
